import * as React from "react";
import { DevToolID, setDevTool } from "./devTools";

/**
 * Shared frame for dev toolbar panels: dark card above the toolbar with a
 * title, optional extra header buttons, "hide" (fold to the header) and ✕
 * (turn the tool off; toggle it back from the toolbar). Every tool panel uses
 * it so they all look and behave the same.
 *
 * Plain inline styles on purpose: it must look the same on every brand theme.
 */
export function DevPanel({
  toolID,
  title,
  side,
  actions,
  children,
}: {
  toolID: DevToolID;
  title: string;
  /** Tracking sits right, quiz slides left, so both can be open on desktop. */
  side: "left" | "right";
  actions?: React.ReactNode;
  children: React.ReactNode;
}) {
  const [folded, setFolded] = React.useState(false);

  return (
    <div
      style={{ ...devPanelStyles.panel, [side]: 8 }}
      data-testid={`dev-panel-${toolID}`}
    >
      <div style={devPanelStyles.header}>
        <strong>{title}</strong>
        <span style={{ display: "flex", gap: 6 }}>
          {actions}
          <DevButton onClick={() => setFolded((f) => !f)}>{folded ? "open" : "hide"}</DevButton>
          <DevButton
            onClick={() => setDevTool(toolID, false)}
            title="Close (toggle it back from the dev toolbar)"
          >
            ✕
          </DevButton>
        </span>
      </div>
      {!folded && <div style={devPanelStyles.body}>{children}</div>}
    </div>
  );
}

export function DevButton(props: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return <button {...props} style={{ ...devPanelStyles.button, ...props.style }} />;
}

export const devPanelColors = {
  text: "#e5e7eb",
  muted: "#9ca3af",
  faint: "#6b7280",
  on: "#4ade80",
  warn: "#fbbf24",
  accent: "#93c5fd",
  border: "#374151",
};

export const devPanelStyles: Record<string, React.CSSProperties> = {
  panel: {
    position: "fixed",
    // Above the dev toolbar (28px).
    bottom: 40,
    width: "min(420px, calc(100vw - 16px))",
    maxHeight: "60vh",
    display: "flex",
    flexDirection: "column",
    background: "rgba(17, 24, 39, 0.96)",
    color: devPanelColors.text,
    border: `1px solid ${devPanelColors.border}`,
    borderRadius: 8,
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.4)",
    font: "11px/1.4 ui-monospace, SFMono-Regular, Menlo, monospace",
    textAlign: "left",
    zIndex: 2147483647,
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "6px 8px",
    borderBottom: `1px solid ${devPanelColors.border}`,
  },
  body: { padding: 8, overflowY: "auto" },
  divider: {
    marginTop: 8,
    paddingTop: 6,
    borderTop: `1px solid ${devPanelColors.border}`,
    color: devPanelColors.muted,
  },
  button: {
    background: devPanelColors.border,
    color: devPanelColors.text,
    border: "none",
    borderRadius: 4,
    padding: "2px 6px",
    font: "inherit",
    cursor: "pointer",
  },
};
